import "./mainList.css";

import { Button, Row } from "reactstrap";
import {
  HIPHEN_GREEN,
  MISSION_MONITORING_FLAG_COLORS,
  MISSION_MONITORING_STATIC_STATUS_ICONS,
} from "../../../constants";
import {
  StatusCounter,
  textOn,
} from "../../../clientDashboard/components/StatusCounter";
import {
  countMissionsWithResults,
  dateSort,
  formatDate,
  groupBy,
  humanize,
  missionHasResults,
  missionRequiresClientAction,
  occurrences,
  sumKey,
} from "../../../utils";
import { useMemo, useState } from "react";

import { ComponentPlaceholder } from "../../../components/componentPlaceholder/componentPlaceholder";
import CopyButton from "../../../components/copyButton";
import { CountryFlag } from "../../../components/CountryFlag/CountryFlag";
import { MissionDetailsCard } from "../missionDetailsCard/missionDetailsCard";
import PropTypes from "prop-types";
import classnames from "classnames";
import { fetchAcquisitionReports } from "../../../services/backendRequests";
import { hasBackofficeAccess } from "../../../services/roles";
import { useHistory } from "react-router-dom";

const DetailsCounters = ({
  element,
  resultsAvailable,
  openResultsMap,
  resultsCount,
}) => {
  return (
    <div className="date-details">
      <span className="date-details__counter">
        {resultsAvailable && (
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-content="View results"
            className="p-0"
            color="link"
            onClick={(e) => {
              e.stopPropagation();
              openResultsMap();
            }}
          >
            <i className="fa-solid fa-earth-americas" />
          </Button>
        )}
        {resultsCount !== undefined &&
          (resultsCount === 0 ? (
            <>
              <i className="fa-solid fa-earth-americas discrete-icon" />
              {resultsCount}
            </>
          ) : (
            <>
              <i
                style={{ color: HIPHEN_GREEN }}
                className="fa-solid fa-earth-americas"
              />
              {resultsCount}
            </>
          ))}
      </span>
      <span className="date-details__counter">
        {element.count_active_flags === 0 ? (
          <>
            <i className="fa-solid fa-flag discrete-icon" />
            {element.count_active_flags}
          </>
        ) : (
          <>
            <i
              style={{ color: MISSION_MONITORING_FLAG_COLORS.RAISED }}
              className="fa-solid fa-flag"
            />
            {element.count_active_flags}
          </>
        )}
      </span>
      <span className="date-details__counter">
        {element.count_unread_comments === 0 ? (
          <>
            <i className="fa-solid fa-comment-dots discrete-icon" />
            {element.count_unread_comments}
          </>
        ) : (
          <>
            <i
              style={{ color: HIPHEN_GREEN }}
              className="fa-solid fa-comment-dots"
            />
            {element.count_unread_comments}
          </>
        )}
      </span>
    </div>
  );
};

// Exported so the campaign > site view can reuse the exact same expandable
// date row (header + inline MissionDetailsCard) as the monitoring page.
export const ElementItem = ({
  element,
  getStatusColor,
  refresh,
  user,
  acquisitionReportResults,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const history = useHistory();

  const date = formatDate(element.acquisition_date);
  const resultsAvailable = useMemo(
    () => missionHasResults(element, user.trials),
    [element, user.trials]
  );
  const openResultsMap = () => {
    history.push(`/map/${element.site.id}/${date}`);
  };

  const elementClasses = classnames("date-item powerdash-component clickable", {
    active: !collapsed,
    "date-item--action": missionRequiresClientAction(element),
  });

  return (
    <div className="date-item-wrapper">
      <div className={elementClasses} onClick={() => setCollapsed(!collapsed)}>
        <span className="date-item-name">
          <i
            className={`fa-solid ${
              MISSION_MONITORING_STATIC_STATUS_ICONS[
                element.status.replaceAll(" ", "_")
              ] ?? "fa-circle"
            }`}
            style={{
              color:
                getStatusColor(element.substatus) ??
                getStatusColor(element.status),
            }}
          />{" "}
          {date}{" "}
          {hasBackofficeAccess(user.self) && (
            <CopyButton
              small
              value={element.id}
              datatip="Copy mission monitoring uuid"
            />
          )}
        </span>
        <span className="date-item-column">
          <i className="fa-solid fa-camera discrete-icon" />
          <span>{element.sensor_type}</span>
        </span>
        <span className="date-item-column">
          <span>
            <i className="fa-solid fa-leaf discrete-icon" /> {element.crop}
          </span>
        </span>
        <div className="date-item-column">
          <span
            style={{
              background: getStatusColor(element.status),
              color: textOn(getStatusColor(element.status)),
              borderRadius: "10px",
              padding: "1px 8px",
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {humanize(element.status)}
          </span>
        </div>
        <span
          className="date-item-column"
          style={{
            color:
              getStatusColor(element.substatus) ??
              getStatusColor(element.status),
            fontWeight: "bold",
          }}
        >
          {humanize(element.substatus)}
        </span>
        <DetailsCounters
          element={element}
          resultsAvailable={resultsAvailable}
          openResultsMap={openResultsMap}
        />
      </div>
      {!collapsed && (
        <MissionDetailsCard
          element={element}
          getStatusColor={getStatusColor}
          refresh={refresh}
          user={user}
          acquisitionReportResults={acquisitionReportResults}
          resultsAvailable={resultsAvailable}
          openResultsMap={openResultsMap}
          date={date}
        />
      )}
    </div>
  );
};

const SiteItem = ({
  site,
  elements,
  statusList,
  getStatusColor,
  refresh,
  user,
}) => {
  const statusCounts = occurrences(statusList, elements, "status");
  const siteStatusSegments = statusList
    .map((status) => ({
      label: humanize(status),
      value: statusCounts[status] ?? 0,
      color: getStatusColor(status),
      filterValue: status,
    }))
    .filter((segment) => segment.value > 0);
  const [collapsed, setCollapsed] = useState(true);
  const [acquisitionReportResults, setAcquisitionReportResults] =
    useState(null);
  const resultsCount = countMissionsWithResults(elements, user.trials);
  // Surface at the (collapsed-by-default) site level that one of its missions
  // needs the client to act, mirroring the campaign card's "action" highlight.
  const needsAction = elements?.some(missionRequiresClientAction);
  const onClickAcquisitionReport = async () => {
    setCollapsed(!collapsed);
    try {
      const results = await fetchAcquisitionReports(user, site.id);
      setAcquisitionReportResults(results);
    } catch {}
  };

  return (
    <>
      <Row
        className={classnames("site-item powerdash-component clickable", {
          "site-item--action": needsAction,
        })}
        onClick={onClickAcquisitionReport}
      >
        <CountryFlag alpha={site.country} />
        <div className="site-labels">
          <span className="site-name">{site.display_name}</span>
          {hasBackofficeAccess(user.self) && (
            <div className="ms-1">
              <CopyButton small value={site.id} datatip="Copy site uuid" />
            </div>
          )}
        </div>
        <div className="status-badges">
          <StatusCounter segments={siteStatusSegments} />
        </div>
        <DetailsCounters
          element={{
            count_active_flags: sumKey(elements, "count_active_flags"),
            count_unread_comments: sumKey(elements, "count_unread_comments"),
          }}
          resultsCount={resultsCount}
        />
      </Row>
      <div className="collapse-list">
        {!collapsed &&
          elements !== undefined &&
          elements
            ?.sort((a, b) => {
              return dateSort(a.acquisition_date, b.acquisition_date);
            })
            .map((element) => (
              <ElementItem
                key={element.id}
                element={element}
                getStatusColor={getStatusColor}
                refresh={refresh}
                user={user}
                acquisitionReportResults={acquisitionReportResults}
              />
            ))}
      </div>
    </>
  );
};

const ContractItem = ({
  name,
  elements,
  statusList,
  getStatusColor,
  refresh,
  user,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const caretClasses = classnames("fa-solid fa-caret-right", {
    "fa-rotate-90": !collapsed,
  });
  return (
    <>
      <div
        className="clickable contract-item"
        title={name}
        onClick={() => setCollapsed(!collapsed)}
      >
        <h3>
          <i className={caretClasses} />
        </h3>
        <h3>{name}</h3>
      </div>
      {!collapsed &&
        Object.entries(groupBy(elements, "site", "id"))
          ?.sort((a, b) => {
            // Comparing sites of first elements in element arrays
            // because every element should has the same site
            const aname = a[1][0].site?.display_name.toLowerCase();
            const bname = b[1][0].site?.display_name.toLowerCase();
            if (aname < bname) return -1;

            if (aname > bname) return 1;

            return 0;
          })
          .map((element) => (
            <SiteItem
              key={element[0]}
              site={element[1][0].site}
              elements={element[1]}
              statusList={statusList}
              getStatusColor={getStatusColor}
              refresh={refresh}
              user={user}
            />
          ))}
    </>
  );
};

/*
 * Main list displays mission monitoring collapsed below site and contract levels
 * every item show its associated mision details card
 */
export const MainList = ({
  contracts,
  elements,
  statusList,
  getStatusColor,
  refresh,
  user,
}) => {
  return (
    <div className="main-list">
      {elements.length === 0 ? (
        <ComponentPlaceholder
          icon="fa-solid fa-file-lines"
          text="No campaign selected or no data"
        />
      ) : (
        <div className="overflow-list-wrapper">
          <div className="date-list">
            {Object.entries(groupBy(elements, "contract_info", "id"))?.map(
              (contract) => (
                <ContractItem
                  key={contract[0]}
                  name={contracts.find((c) => c.id === contract[0])?.name ?? ""}
                  elements={contract[1]}
                  statusList={statusList}
                  getStatusColor={getStatusColor}
                  refresh={refresh}
                  user={user}
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ElementItem.propTypes = {
  element: PropTypes.object.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};

SiteItem.propTypes = {
  site: PropTypes.object.isRequired,
  elements: PropTypes.array.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  statusList: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
};

ContractItem.propTypes = {
  name: PropTypes.string.isRequired,
  elements: PropTypes.array.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  statusList: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
};

MainList.propTypes = {
  contracts: PropTypes.array.isRequired,
  elements: PropTypes.array.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  statusList: PropTypes.array.isRequired,
  refresh: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};
